import {
  articles,
  rassegne,
  sources,
  tierLabels,
  Sentiment,
  Tier,
} from './data'

// ---------------------------------------------------------------------------
// Predictive analysis engine + in-memory store (PROTOTYPE)
// PRODUCTION NOTE: in produzione il motore chiamerebbe un modello predittivo
// addestrato sullo storico delle rassegne (relevanceScore, AVE, sentiment,
// tier, testate) combinato con un LLM che estrae i temi dal testo del
// comunicato allegato. Qui usiamo un'euristica deterministica sui temi
// inferiti dal nome del file + le baseline storiche, con razionali espliciti
// per ogni widget così da poter spiegare il "perché" all'utente.
// ---------------------------------------------------------------------------

export interface TierReach {
  tier: Tier
  label: string
  projectedArticles: number
  share: number
}

export interface WidgetRationale {
  // Why this value? + on which data is it based
  why: string
  dataBasis: string[]
}

export interface PredictiveAnalysis {
  id: string
  fileName: string
  fileSize: string
  createdAt: string // ISO
  predictedScore: number // 0–10
  aveLow: number
  aveHigh: number
  expectedSentiment: Sentiment
  sentimentConfidence: number // 0–100
  projectedArticles: number
  reachByTier: TierReach[]
  likelySources: string[]
  confidence: 'high' | 'medium' | 'low'
  themes: string[]
  drivers: string[]
  rationales: {
    score: WidgetRationale
    ave: WidgetRationale
    articles: WidgetRationale
    sentiment: WidgetRationale
    tier: WidgetRationale
    sources: WidgetRationale
  }
}

// --- Historical baselines (computed once from the corpus) ---
export const baselines = (() => {
  const total = articles.length || 1
  const avgScore = articles.reduce((a, x) => a + x.relevanceScore, 0) / total
  const avgAVE = articles.reduce((a, x) => a + x.ave, 0) / total
  const avgArticlesPerRassegna =
    rassegne.reduce((a, r) => a + r.articleCount, 0) / (rassegne.length || 1)
  return {
    avgScore,
    avgAVE,
    avgArticlesPerRassegna,
    articleCount: articles.length,
    rassegnaCount: rassegne.length,
  }
})()

const themeWeights: {
  label: string
  keywords: string[]
  score: number
  sentiment: Sentiment
}[] = [
  {
    label: 'Memoria / Shoah',
    keywords: ['memoria', 'shoah', 'olocausto', 'segre', 'auschwitz'],
    score: 2.5,
    sentiment: 'positive',
  },
  {
    label: 'Antisemitismo',
    keywords: ['antisemitismo', 'odio', 'discriminazione', 'razzismo'],
    score: 2.0,
    sentiment: 'negative',
  },
  {
    label: 'Istituzioni',
    keywords: ['istituzion', 'presidente', 'ministro', 'governo', 'mattarella'],
    score: 1.8,
    sentiment: 'neutral',
  },
  {
    label: 'Cultura',
    keywords: [
      'cultura',
      'festival',
      'museo',
      'sinagoga',
      'mostra',
      'libro',
      'meis',
    ],
    score: 1.2,
    sentiment: 'positive',
  },
  {
    label: 'Sicurezza',
    keywords: ['sicurezza', 'protocollo', 'minacc', 'attacco'],
    score: 1.5,
    sentiment: 'neutral',
  },
  {
    label: 'Comunità',
    keywords: ['comunità', 'ucei', 'ebraic'],
    score: 1.0,
    sentiment: 'positive',
  },
]

function fmtK(n: number) {
  return `€${(n / 1000).toFixed(0)}k`
}

export function predict(
  fileName: string,
  fileSize: string,
  id?: string,
  createdAt?: string,
): PredictiveAnalysis {
  const text = fileName.toLowerCase()
  const matched = themeWeights.filter((t) =>
    t.keywords.some((k) => text.includes(k)),
  )
  const themeBoost = matched.reduce((a, t) => a + t.score, 0)
  const themes = matched.map((m) => m.label)

  const rawScore = baselines.avgScore * 0.55 + themeBoost + 0.6
  const predictedScore = Math.max(2, Math.min(10, Number(rawScore.toFixed(1))))

  const expectedSentiment: Sentiment =
    matched.length > 0
      ? matched.slice().sort((a, b) => b.score - a.score)[0].sentiment
      : 'neutral'

  const reachFactor = predictedScore / 10
  const projectedArticles = Math.max(
    3,
    Math.round(baselines.avgArticlesPerRassegna * reachFactor),
  )

  const aveCenter = baselines.avgAVE * (0.6 + reachFactor)
  const aveLow = Math.round((aveCenter * 0.7) / 1000) * 1000
  const aveHigh = Math.round((aveCenter * 1.35) / 1000) * 1000

  const reachByTier: TierReach[] = (
    [
      { tier: 1 as Tier, label: tierLabels[1] },
      { tier: 2 as Tier, label: tierLabels[2] },
      { tier: 3 as Tier, label: tierLabels[3] },
      { tier: 'specialized' as Tier, label: tierLabels['specialized'] },
    ] as const
  ).map((t) => {
    let share = 0.1
    if (t.tier === 1) share = reachFactor >= 0.6 ? 0.32 : 0.12
    else if (t.tier === 2) share = 0.26
    else if (t.tier === 3) share = 0.18
    else share = 0.34
    return {
      ...t,
      share,
      projectedArticles: Math.max(0, Math.round(projectedArticles * share)),
    }
  })

  const likelySources = sources
    .slice()
    .sort((a, b) => (a.tier === 'specialized' ? -1 : 0))
    .slice(0, 4)
    .map((s) => s.name)

  const confidence: PredictiveAnalysis['confidence'] =
    matched.length >= 2 ? 'high' : matched.length === 1 ? 'medium' : 'low'

  const drivers: string[] = []
  if (themes.length > 0) drivers.push(`Temi rilevati: ${themes.join(', ')}`)
  else drivers.push('Nessun tema storico forte rilevato: stima prudenziale')
  drivers.push(
    `Baseline storica: score ${baselines.avgScore.toFixed(1)} · AVE medio ${fmtK(
      baselines.avgAVE,
    )}`,
  )
  if (reachFactor >= 0.6)
    drivers.push('Probabile ripresa anche da testate Tier 1 nazionali')

  const themeList = themes.length ? themes.join(', ') : 'nessun tema forte'

  return {
    id: id || `pa-${Date.now()}`,
    fileName,
    fileSize,
    createdAt: createdAt || new Date().toISOString(),
    predictedScore,
    aveLow,
    aveHigh,
    expectedSentiment,
    sentimentConfidence:
      confidence === 'high' ? 82 : confidence === 'medium' ? 64 : 45,
    projectedArticles,
    reachByTier,
    likelySources,
    confidence,
    themes,
    drivers,
    rationales: {
      score: {
        why: `Lo score previsto (${predictedScore.toFixed(
          1,
        )}/10) parte dalla media storica di rilevanza (${baselines.avgScore.toFixed(
          1,
        )}) e viene corretto in base ai temi rilevati nel comunicato (${themeList}), che storicamente generano una copertura ${
          reachFactor >= 0.6 ? 'elevata' : 'moderata'
        }.`,
        dataBasis: [
          `${baselines.articleCount} articoli storici analizzati`,
          `Score medio storico: ${baselines.avgScore.toFixed(1)}/10`,
          `Temi rilevati: ${themeList}`,
        ],
      },
      ave: {
        why: `L'AVE stimato (${fmtK(aveLow)}–${fmtK(
          aveHigh,
        )}) deriva dall'AVE medio storico per articolo (${fmtK(
          baselines.avgAVE,
        )}) proiettato sul numero di uscite previste, con un intervallo che riflette l'incertezza della stima.`,
        dataBasis: [
          `AVE medio storico per articolo: ${fmtK(baselines.avgAVE)}`,
          `Uscite previste: ~${projectedArticles}`,
          'Intervallo -30% / +35% per incertezza',
        ],
      },
      articles: {
        why: `Le uscite previste (~${projectedArticles}) sono ottenute scalando la reach media per rassegna (${Math.round(
          baselines.avgArticlesPerRassegna,
        )} articoli) in proporzione allo score previsto.`,
        dataBasis: [
          `Reach media storica: ${Math.round(
            baselines.avgArticlesPerRassegna,
          )} articoli/rassegna`,
          `${baselines.rassegnaCount} rassegne storiche`,
          `Fattore di reach: ${(reachFactor * 100).toFixed(0)}%`,
        ],
      },
      sentiment: {
        why: `Il sentiment atteso (${expectedSentiment}) corrisponde al tono prevalente dei temi rilevati nel comunicato. La confidenza riflette quanti temi storici riconoscibili sono presenti.`,
        dataBasis: [
          `Temi rilevati: ${themeList}`,
          'Mappatura tema → sentiment basata sullo storico',
          `Confidenza: ${confidence === 'high' ? 'alta' : confidence === 'medium' ? 'media' : 'bassa'}`,
        ],
      },
      tier: {
        why: `La distribuzione per tier stima quante uscite arriveranno su ciascuna fascia di testate. Con uno score di ${predictedScore.toFixed(
          1,
        )}, la ripresa da testate Tier 1 è ${
          reachFactor >= 0.6 ? 'probabile' : 'limitata'
        }; la stampa specializzata ebraica copre quasi sempre i comunicati UCEI.`,
        dataBasis: [
          'Distribuzione storica delle uscite per tier',
          `Score previsto: ${predictedScore.toFixed(1)}/10`,
          'La stampa specializzata copre ~34% delle uscite',
        ],
      },
      sources: {
        why: `Le testate più probabili sono selezionate dallo storico privilegiando la stampa specializzata ebraica e le testate Tier 1 che hanno ripreso comunicati simili per tema.`,
        dataBasis: [
          `${sources.length} testate monitorate`,
          'Priorità a stampa specializzata + Tier 1',
          `Temi affini: ${themeList}`,
        ],
      },
    },
  }
}

// --- In-memory store (prototype, seeded with example analyses) ---
const SEED_FILES: { name: string; size: string; daysAgo: number }[] = [
  {
    name: 'Comunicato — Giorno della Memoria 2026.pdf',
    size: '248 KB',
    daysAgo: 2,
  },
  {
    name: 'Comunicato — Nuova mostra al MEIS di Ferrara.docx',
    size: '96 KB',
    daysAgo: 5,
  },
  {
    name: 'Comunicato — Protocollo sicurezza scuole ebraiche.pdf',
    size: '184 KB',
    daysAgo: 9,
  },
  {
    name: 'Comunicato — Festival della Cultura Ebraica.pdf',
    size: '312 KB',
    daysAgo: 14,
  },
]

let store: PredictiveAnalysis[] = SEED_FILES.map((f, i) => {
  const d = new Date()
  d.setDate(d.getDate() - f.daysAgo)
  return predict(f.name, f.size, `pa-seed-${i + 1}`, d.toISOString())
})

export function getAnalyses(): PredictiveAnalysis[] {
  return store
    .slice()
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
}

export function getAnalysis(id: string): PredictiveAnalysis | undefined {
  return store.find((a) => a.id === id)
}

export function addAnalysis(a: PredictiveAnalysis) {
  store = [a, ...store]
}

// --- UI helpers (shared) ---
export function sentimentColorClass(s: Sentiment) {
  return s === 'positive'
    ? 'bg-[#4A7C59]'
    : s === 'negative'
      ? 'bg-[#B85450]'
      : 'bg-[#64B5F6]'
}
export function confidenceLabel(c: PredictiveAnalysis['confidence']) {
  return c === 'high' ? 'Alta' : c === 'medium' ? 'Media' : 'Bassa'
}
export function confidenceBadgeClass(c: PredictiveAnalysis['confidence']) {
  return c === 'high'
    ? 'bg-emerald-50 text-emerald-700'
    : c === 'medium'
      ? 'bg-amber-50 text-amber-700'
      : 'bg-muted text-muted-foreground'
}
